import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { Subscription } from 'rxjs';

// Services
import { MediaAssetService } from '../../../services/media-asset.service';

// Components
import { ButtonComponent } from '../../../../../../shared/_components/button/button.component';
import { FlowMediaUploadStep } from '../../flows/flow-media-upload/step/flow-media-upload-step.component';
import { GetAssetStatusResponse } from '@oddr/user-services';
import { GroupStatusResponse } from '../../../models/media-upload.model';
import { Statuses } from '@oddr/user-schemas';
import { MediaThumbnailHelper } from '../../../../../../shared/_helpers/media-thumbnail.helper';

@Component({
  selector: 'pp-step-media-image-complete',
  templateUrl: './step-media-image-complete.component.html',
  styleUrl: './step-media-image-complete.component.scss',
  imports: [CommonModule, ButtonComponent],
})
export class StepMediaImageCompleteComponent extends FlowMediaUploadStep {

  // Component handler (!IMPORTANT - Must match step identifier for this flow)
  identifier = 'mediaUploadFlowStepImageComplete';

  // Aliases
  statuses = Statuses;

  // Form handlers
  pollingStatusUpdate?: GetAssetStatusResponse;

  // Media variables
  thumbnail!: string;

  // Services
  private readonly mediaAssetService = inject(MediaAssetService);

  // Subscriptions
  private readonly processingPollingStatus$?: Subscription;
  private readonly groupSigningStatus$?: Subscription;

  // Group upload state
  groupSigningComplete = false;
  groupSigningProgress: GroupStatusResponse | null = null;

  constructor() {
    super();

    this.processingPollingStatus$ = this.mediaUploadService.processingPollingStatus$.subscribe({
      next: (response: GetAssetStatusResponse) => {
        this.pollingStatusUpdate = response;
        if (this.mediaUploadService.isGroupUpload && response.c2paStatus === Statuses.Complete) {
          this.groupSigningComplete = true;
        }
        this.cdr.markForCheck();
      },
    });

    this.groupSigningStatus$ = this.mediaUploadService.groupSigningStatus$.subscribe({
      next: (status: GroupStatusResponse | null) => {
        this.groupSigningProgress = status;
        this.cdr.markForCheck();
      },
    });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.processingPollingStatus$?.unsubscribe();
    this.groupSigningStatus$?.unsubscribe();
  }

  /**
   * The generated ODDR link where this media item can be accessed.
   * @type {string}
   * @memberof StepMediaImageCompleteComponent
   */
  get oddrLink(): string {
    return `${location.origin}/m/${this.operation?.assetId}`;
  }

  /**
   * What to call the upload in copy — this step also reviews PDFs and audio.
   * @type {string}
   * @memberof StepMediaImageCompleteComponent
   */
  get mediaNoun(): string {
    const contentType = this.operation?.contentType ?? '';
    if (MediaThumbnailHelper.isDocument(contentType)) return 'document';
    if (MediaThumbnailHelper.isAudio(contentType)) return 'audio file';
    return 'image';
  }

  /**
   * Copies the thumbnail URL to the clipboard.
   * @memberof StepMediaImageCompleteComponent
   */
  copyThumbnailUrlToClipboard(): void {
    navigator.clipboard.writeText(this.oddrLink);
  }

  /**
   * Downloads the C2PA signed copy file.
   * @memberof StepMediaImageCompleteComponent
   */
  downloadC2paSignedCopy(): void {
    if (this.operation) this.mediaAssetService.downloadC2paSignedCopy(this.operation?.assetId).subscribe();
  }

  /**
   * Returns the file path to the asset to display depending on the provided C2PA status.
   * @param status the current C2PA status.
   * @memberof StepMediaImageCompleteComponent
   */
  resolveC2paStatusIcon(status: any): string {
    switch (status) {
      case Statuses.Failed:
        return this.resolveImageIcon('circle-question-mark')
      case Statuses.Complete:
        return '/assets/images/icons/check-circle-solid-primary.svg';
      default:
        return this.resolveImageIcon('time');
    }
  }

  /**
   * Submits and ends the flow.
   * @memberof StepMediaImageCompleteComponent
   */
  get canSubmit(): boolean {
    if (this.mediaUploadService.isGroupUpload) {
      return this.groupSigningComplete;
    }
    return true;
  }

  submitAndCompleteFlow(): void {
    if (!this.canSubmit) return;
    this.mediaUploadService.postUploadConfigurationForIntegrations().subscribe({
      next: () => this.completeFlow(),
    });
  }
}
